<template>
  <WdsStack direction-column gap-md class="tw-mt-4">
    <WdsDivider align-left>
      <WdsText :text="$t('REVIEW')" />
    </WdsDivider>

    <div class="tw-bg-true-gray-100 tw-rounded tw-p-4">
      <div
        v-if="loading"
        class="tw-flex tw-justify-center tw-items-center tw-py-6"
        :data-test="`${dataTestPrefix}-loading`"
      >
        <WdsSpinner :size="20" indeterminate />
      </div>
      <template v-else>
        <WdsRadioGroup v-model:checked="decision" :name="`${dataTestPrefix}-decision`">
          <WdsRadio value="CLEARED" :data-test="`${dataTestPrefix}-cleared`">
            {{ $t('Mark as cleared') }}
            <WdsText :sub-text="clearedSubText" />
          </WdsRadio>
          <WdsRadio value="CHANGES_REQUESTED" :data-test="`${dataTestPrefix}-changes-requested`">
            {{ $t('Request changes') }}
            <WdsText :sub-text="changesRequestedSubText" />
          </WdsRadio>
        </WdsRadioGroup>

        <WdsInputArea
          v-if="decision === 'CHANGES_REQUESTED'"
          v-model="note"
          :label="noteLabel"
          :height="'96px'"
          resizeable
          :data-test="`${dataTestPrefix}-note`"
        />
      </template>
    </div>
    <WdsAlert v-if="errorMessage" error>{{ errorMessage }}</WdsAlert>

    <WdsStack direction-row justify-space-between gap-sm class="tw-mt-2">
      <WdsButton tertiary :data-test="`${dataTestPrefix}-cancel`" @click="$emit('cancel')">
        {{ $t('Cancel') }}
      </WdsButton>
      <WdsButton
        primary
        :disabled="!canSubmit || submitting"
        :data-test="`${dataTestPrefix}-save`"
        @click="handleSubmit"
      >
        <WdsSpinner v-if="submitting" :size="14" indeterminate />
        <template v-else>{{ $t('Save') }}</template>
      </WdsButton>
    </WdsStack>
  </WdsStack>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import {
  WdsAlert,
  WdsButton,
  WdsDivider,
  WdsInputArea,
  WdsRadio,
  WdsRadioGroup,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

// CLEARED / CHANGES_REQUESTED match both WorkerReviewDecision and
// RecruiterReviewDecision GraphQL enums; the wrapper casts back to its
// specific enum when calling the mutation.
export type ReviewDecision = 'CLEARED' | 'CHANGES_REQUESTED'

const props = defineProps<{
  loading?: boolean
  submitting: boolean
  errorMessage: string
  initialDecision?: ReviewDecision | null
  initialNote?: string
  dataTestPrefix: string
  clearedSubText: string
  changesRequestedSubText: string
  noteLabel: string
}>()

const emit = defineEmits<{
  submit: [{ decision: ReviewDecision; note: string | null }]
  cancel: []
}>()

const decision = ref<ReviewDecision | ''>('')
const note = ref('')

watch(
  () => [props.initialDecision, props.initialNote] as const,
  ([d, n]) => {
    if (decision.value === '' && d) decision.value = d
    if (note.value === '' && n) note.value = n
  },
  { immediate: true },
)

const canSubmit = computed(() => {
  if (props.loading) return false
  if (decision.value === '') return false
  if (decision.value === 'CHANGES_REQUESTED' && note.value.trim() === '') return false
  return true
})

function handleSubmit() {
  if (decision.value === '') return
  const trimmed = decision.value === 'CHANGES_REQUESTED' ? note.value.trim() : null
  emit('submit', { decision: decision.value, note: trimmed })
}
</script>
